import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useConsentStore } from '@/stores/consentStore';
import { useBankAccountStore } from '@/stores/bankAccountStore';
import { useAuthStore } from '@/stores/authStore';
import { apiService } from '@/services/api';
import { signIn } from '@/services/auth';

export function ConsentCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addConsent, fetchConsents } = useConsentStore();
  const { syncAccount, finalizeConsent } = useBankAccountStore();
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [status, setStatus] = useState<'loading' | 'syncing' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing consent...');
  const [consentId, setConsentId] = useState<string | null>(null);
  const [aspspCde, setAspspCde] = useState<string | null>(null);
  const processed = useRef(false);

  useEffect(() => {
    // Wait for the app's auth check: it also issues the CSRF token for the current session. A
    // POST sent alongside it echoes the token minted before login and the BFF rejects it (400).
    // With no session (the bank returns to the canonical host), log in first and come back here.
    if (authLoading) return;
    if (!isAuthenticated) {
      signIn();
      return;
    }
    if (processed.current) return;
    processed.current = true;

    const processCallback = async () => {
      // Get consent info from query parameters
      // SIBS integration service uses: consentId, aspspCde, accountId
      // SIBS OAuth callback uses: code, state, error, error_description
      const consentIdParam = searchParams.get('consentId');
      const aspspCdeParam = searchParams.get('aspspCde');
      const accountIdParam = searchParams.get('accountId');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const consentStatusParam = searchParams.get('consent_status');
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      // Resolve which account we're finalizing: prefer the redirect param, else the
      // context stored when initiating consent (survives SIBS error redirects that omit it).
      let finalizeAccountId: number | null = accountIdParam ? parseInt(accountIdParam, 10) : null;
      if (!finalizeAccountId) {
        try {
          const pending = JSON.parse(sessionStorage.getItem('pendingConsent') || 'null');
          if (pending?.accountId) finalizeAccountId = Number(pending.accountId);
        } catch { /* ignore */ }
      }

      // Revolut's redirect names the consent, not our account, and may land on another host than the
      // one the user started from. Ask the backend, ignoring any other bank's pending context.
      if (aspspCdeParam === 'REVOGB21' && consentIdParam) {
        try {
          const response = await apiService.get<number>(`/revolut/consent/${encodeURIComponent(consentIdParam)}/account`);
          finalizeAccountId = response.value ?? null;
        } catch {
          finalizeAccountId = null;
        }
        if (!finalizeAccountId) {
          setStatus('error');
          setMessage('We could not find the account for this authorization. Please try again from the accounts page.');
          return;
        }
      }

      // Decoupled acquisition: finalize the consent by the account's stored ConsentId,
      // independent of the SIBS bank-redirect callback (which can fail on RequestId match).
      if (finalizeAccountId) {
        try {
          setMessage('Confirming your bank consent...');
          const result = await finalizeConsent(finalizeAccountId);
          try { sessionStorage.removeItem('pendingConsent'); } catch { /* ignore */ }

          if (result?.connected) {
            if (consentIdParam) setConsentId(consentIdParam);
            if (aspspCdeParam) setAspspCde(aspspCdeParam);
            await fetchConsents();
            // Transaction sync is a separate, retryable step — it never blocks "connected".
            setStatus('syncing');
            setMessage('Account connected. Syncing transactions...');
            try {
              await syncAccount(finalizeAccountId);
              setStatus('success');
              setMessage('Bank account connected and synced!');
            } catch {
              setStatus('success');
              setMessage('Bank account connected. Transaction sync will retry automatically.');
            }
            return;
          }

          // Consent not valid yet — surface the bank's status (from finalize or the redirect).
          const statusLower = (result?.consentStatus || consentStatusParam || '').toLowerCase();
          setStatus('error');
          if (statusLower === 'revokedbypsu') setMessage('You cancelled the authorization at your bank. Please try again.');
          else if (statusLower === 'expired') setMessage('The authorization session expired. Please try again.');
          else if (statusLower === 'rejected') setMessage('Your bank rejected the authorization request. Please try again.');
          else if (statusLower === 'received' || statusLower === 'pending') setMessage('Authorization is still pending. Please complete it at your bank, then retry.');
          else setMessage(result?.message || errorDescription || 'Consent could not be confirmed. Please try again.');
          return;
        } catch (err) {
          setStatus('error');
          setMessage(err instanceof Error ? err.message : 'Failed to confirm consent. Please try again.');
          return;
        }
      }

      // Handle OAuth/SIBS redirect error
      if (error) {
        setStatus('error');
        // Provide user-friendly messages based on consent status
        const statusLower = consentStatusParam?.toLowerCase();
        if (statusLower === 'revokedbypsu') {
          setMessage('You cancelled the authorization at your bank. Please try again.');
        } else if (statusLower === 'expired') {
          setMessage('The authorization session expired. Please try again.');
        } else if (statusLower === 'rejected') {
          setMessage('Your bank rejected the authorization request. Please try again.');
        } else if (statusLower === 'received' || statusLower === 'pending') {
          setMessage('Authorization is still pending. Please complete the authorization at your bank.');
        } else {
          setMessage(errorDescription || `Authorization failed: ${error}`);
        }
        return;
      }

      // Handle SIBS OAuth callback with code parameter
      // This happens when SIBS redirects back after bank authorization
      if (code && !consentIdParam) {
        // Try to extract consent info from state parameter (format: "consentId|aspspCde|clientId|accountId")
        if (state) {
          const stateParts = state.split('|');
          if (stateParts.length >= 2) {
            // Call the backend API to process the callback
            try {
              setMessage('Processing authorization...');
              const response = await apiService.get<{
                success: boolean;
                consentId: string;
                status: string;
                error?: string;
              }>('/consent/callback', { code, state });

              if (response.value) {
                if (response.value.success) {
                  setConsentId(response.value.consentId);
                  setStatus('success');
                  setMessage('Bank account connected successfully!');
                  return;
                } else {
                  setStatus('error');
                  // Show user-friendly message for common statuses
                  const statusLower = response.value.status?.toLowerCase();
                  if (statusLower === 'revokedbypsu') {
                    setMessage('You cancelled the authorization at your bank. Please try again.');
                  } else if (statusLower === 'expired') {
                    setMessage('The authorization session expired. Please try again.');
                  } else if (statusLower === 'rejected') {
                    setMessage('Your bank rejected the authorization request. Please try again.');
                  } else {
                    setMessage(response.value.error || `Authorization status: ${response.value.status}`);
                  }
                  return;
                }
              }
            } catch (err) {
              // If API call fails, show generic error
              setStatus('error');
              setMessage('Failed to complete authorization. Please try again.');
              return;
            }
          }
        }
        // If we can't parse state, show generic error
        setStatus('error');
        setMessage('Authorization session invalid. Please try connecting your account again.');
        return;
      }

      if (!consentIdParam || !aspspCdeParam) {
        setStatus('error');
        setMessage('Missing consent information. Please try again from the accounts page.');
        return;
      }

      setConsentId(consentIdParam);
      setAspspCde(aspspCdeParam);

      // Verify consent status with SIBS API before showing success
      try {
        setMessage('Verifying consent status...');
        const statusResponse = await apiService.getConsentStatus(consentIdParam, aspspCdeParam);

        if (statusResponse.value) {
          const consentStatus = statusResponse.value.consentStatus?.toLowerCase();

          if (consentStatus === 'valid') {
            // Consent is valid - store it temporarily, then refresh from backend for actual validity period
            addConsent(consentIdParam, aspspCdeParam, 'valid');
            // Fetch fresh consents from backend to get actual validUntil from SIBS
            await fetchConsents();

            // Sync the account to fetch balance and activate it
            if (accountIdParam) {
              setStatus('syncing');
              setMessage('Syncing account data...');
              try {
                await syncAccount(parseInt(accountIdParam, 10));
                setStatus('success');
                setMessage('Bank account connected and synced!');
              } catch {
                // Sync failed but consent was stored — user can sync manually later
                setStatus('success');
                setMessage('Bank account connected! Sync will complete shortly.');
              }
            } else {
              setStatus('success');
              setMessage('Bank account connected successfully!');
            }
          } else if (consentStatus === 'received' || consentStatus === 'pending') {
            // Consent is pending authorization
            addConsent(consentIdParam, aspspCdeParam, 'pending');
            setStatus('error');
            setMessage('Consent authorization is pending. Please complete the authorization at your bank.');
          } else {
            // Consent rejected or expired
            setStatus('error');
            setMessage(`Consent was ${consentStatus || 'not authorized'}. Please try again.`);
          }
        } else {
          setStatus('error');
          setMessage('Could not verify consent status. Please try again.');
        }
      } catch (err) {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Failed to verify consent. Please try again.');
      }
    };

    processCallback();
  }, [authLoading, isAuthenticated, searchParams, addConsent, fetchConsents, syncAccount, finalizeConsent]);

  const handleContinue = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Processing...</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'syncing' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Syncing Account...</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-green-800 mb-2">Success!</h2>
              <p className="text-gray-600 mb-2">{message}</p>
              {consentId && (
                <p className="text-sm text-gray-500 mb-2">Consent ID: {consentId}</p>
              )}
              {aspspCde && (
                <p className="text-sm text-gray-500 mb-4">Bank Code: {aspspCde}</p>
              )}
              <button
                onClick={handleContinue}
                className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                View My Accounts
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-red-800 mb-2">Authorization Failed</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <button
                onClick={handleContinue}
                className="mt-4 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Back to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
